import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useRouteLoaderData } from "@remix-run/react";

import { logger } from "~/utils/logging";
import { getArtworkFromData } from "~/utils/data";
import { getSlideIndex } from "~/utils/slides";
import { track } from "~/utils/track";
import { presentReportIssue } from "~/utils/events";
import { useUserContext } from "~/hooks/useUserContext";
import { useLabels } from "~/hooks/useLabels";
import { SlideActionsModal } from "~/components/slideActionsModal/SlideActionsModal";
import { useToasts } from "~/components/toast/useToasts";
import {
  WatchViewViewMode,
  WatchViewSubview,
} from "~/components/watchView/WatchViewManager";
import { UNKNOWN } from "~/constants";

import type { ActionProp } from "~/components/components";
import type { SessionRouteLoaderData } from "./route";

export interface SessionSlidesProps {
  canExpand: boolean;
  currentTime: number;
  disableVideo: boolean;
  fullscreen: boolean;
  paused: boolean;
  viewMode: WatchViewViewMode;
  onRequestSeek: (time: number) => void;
  onRequestSubviewChange: (subview: WatchViewSubview) => void;
  onRequestViewModeChange: (viewMode: WatchViewViewMode) => void;
  onSaveSlideToNotes: (data: { content: string; timestamp: number }) => void;
}

export const SessionSlides = memo(function SessionSlides({
  canExpand,
  currentTime,
  disableVideo,
  fullscreen,
  paused,
  viewMode,
  onRequestSeek,
  onRequestSubviewChange,
  onRequestViewModeChange,
  onSaveSlideToNotes,
}: SessionSlidesProps) {
  const { t } = useLabels();

  const { currentUser, promptLogin } = useUserContext();

  const data = useRouteLoaderData(
    "routes/$class_.sessions.$session",
  ) as SessionRouteLoaderData;

  const [slideData, setSlideData] = useState<string>();

  const { showToast, dismissAllToasts } = useToasts();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleModalRequestClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleViewModeChange = useCallback(
    (viewMode: WatchViewViewMode) => {
      setIsModalOpen(false);
      onRequestViewModeChange(viewMode);
    },
    [onRequestViewModeChange],
  );

  const handleSlideClick = useCallback(() => {
    // Prevent opening if the user is selecting slide text
    const shouldOpen = !globalThis.getSelection()?.toString();

    if (shouldOpen && viewMode !== WatchViewViewMode.Fullscreen) {
      setIsModalOpen(true);
    }
  }, [viewMode]);

  const presentationTheme = useMemo(
    () =>
      JSON.stringify({
        artwork: {
          class: getArtworkFromData(data.session.class?.artwork, "1:1")?.url,
          module: getArtworkFromData(data.session.module?.artwork, "1:1")?.url,
          color: data.session.class?.color,
        },
      }),
    [
      data.session.class?.artwork,
      data.session.class?.color,
      data.session.module?.artwork,
    ],
  );

  const currentSlideIndex = useMemo(
    () => getSlideIndex(currentTime, data.session.slides),
    [currentTime, data.session.slides],
  );

  const actions = useMemo(() => {
    if (!data.session.slides?.length) return [];

    const currentSlide = data.session.slides[currentSlideIndex];

    let actions: ActionProp[] = [];

    if (canExpand) {
      actions.push({
        icon: "arrows-from-line",
        text: t("slide.cta.expand"),
        onClick() {
          handleViewModeChange(WatchViewViewMode.Freestyle);
        },
      });
    }

    if (viewMode === WatchViewViewMode.Freestyle) {
      actions.push({
        icon: "arrows-to-line",
        text: t("slide.cta.collapse"),
        onClick() {
          handleViewModeChange(WatchViewViewMode.Splitscreen);
        },
      });
    }

    actions = actions.concat([
      {
        icon: "rectangle-badge-plus",
        text: t("slide.cta.save"),
        async onClick() {
          if (!currentUser) {
            promptLogin();
            return;
          }

          setIsModalOpen(false);

          try {
            onSaveSlideToNotes({
              content: t("slide.noteTemplate", {
                number: data.session.slides?.[currentSlideIndex].position ?? "",
              }),
              timestamp: currentTime,
            });

            showToast({
              intent: "success",
              title: t("slide.saveNoteToast.success"),
              icon: "check",
              actionText: t("slide.saveNoteToast.cta"),
              onActionClick() {
                dismissAllToasts();
                onRequestSubviewChange(WatchViewSubview.Notebook);
              },
            });

            track("crSlideSave", {
              className: data.session.class?.slug,
              sessionNumber: data.session.position,
              timestamp: currentTime,
            });
          } catch (error) {
            showToast({
              intent: "error",
              title: t("slide.saveNoteToast.error"),
            });

            logger.error("Failed to save slide to notes", error);
          }
        },
      },
      {
        icon: "arrow-rotate-left",
        text: t("slide.cta.playBeginningOfSlide"),
        onClick() {
          onRequestSeek(currentSlide.timestamp);
          setIsModalOpen(false);
        },
      },
    ]);

    if (currentSlideIndex < data.session.slides.length - 1) {
      const nextSlide = data.session.slides[currentSlideIndex + 1];
      actions.push({
        icon: "square-chevron-right",
        text: t("slide.cta.next"),
        onClick() {
          onRequestSeek(nextSlide.timestamp);
          setIsModalOpen(false);
        },
      });
    }

    if (currentSlideIndex > 0) {
      const prevSlide = data.session.slides[currentSlideIndex - 1];

      actions.push({
        icon: "square-chevron-left",
        text: t("slide.cta.previous"),
        onClick() {
          onRequestSeek(prevSlide.timestamp);
          setIsModalOpen(false);
        },
      });
    }

    actions.push({
      icon: "message-exclamation",
      text: t("common.cta.reportIssue"),
      onClick() {
        presentReportIssue({
          url: window.location.href,
          userAgent: window.navigator.userAgent,
          webVersion: window.__env__?.VERSION ?? UNKNOWN,
          session: data.session.position,
          timestamp: currentTime,
          slideId: data.session.slides?.[currentSlideIndex].id,
        }).catch(logger.error);
      },
    });

    return actions;
  }, [
    canExpand,
    currentSlideIndex,
    currentTime,
    currentUser,
    data.session.class?.slug,
    data.session.position,
    data.session.slides,
    dismissAllToasts,
    handleViewModeChange,
    onRequestSeek,
    onRequestSubviewChange,
    onSaveSlideToNotes,
    promptLogin,
    showToast,
    t,
    viewMode,
  ]);

  const decoration = useMemo(() => {
    if (viewMode === WatchViewViewMode.Fullscreen) {
      return fullscreen ? "none" : "rounded";
    }
    return "box-shadow";
  }, [fullscreen, viewMode]);

  // Stringify slide data on the client once
  useEffect(() => {
    setSlideData(JSON.stringify(data.session.slides));
  }, [data.session.slides]);

  return (
    <>
      <bp-slide-presentation
        data={slideData}
        current-time={currentTime}
        decoration={decoration}
        view-mode={
          viewMode === WatchViewViewMode.Freestyle ? "tall" : "default"
        }
        paused={paused}
        theme={presentationTheme}
        no-video={disableVideo ? "true" : "false"}
        onClick={handleSlideClick}
      ></bp-slide-presentation>

      {viewMode !== WatchViewViewMode.Fullscreen && (
        <SlideActionsModal
          actions={actions}
          isOpen={isModalOpen}
          postTitle={t("slide.modal.actions.postTitle", {
            number: data.session.position,
            title: data.session.title,
          })}
          title={t("slide.modal.actions.title", {
            number: data.session.slides?.[currentSlideIndex].position,
          })}
          onRequestClose={handleModalRequestClose}
        />
      )}
    </>
  );
});
